import { Decimal, ORDER_LIMITS, round2, toMoneyString } from '@app/shared';
import type { CartLineView, StoreCart } from './types';

/** Largest quantity we accept (the API stores quantities as Decimal(12,3)). */
const MAX_QTY = new Decimal('999999');
const QTY_DECIMALS = 3;

const DECIMAL_RE = /^\d+(\.\d+)?$/;

/** A plain non-negative decimal string (`"12"`, `"89.90"`) — no sign, exponent, hex or `Infinity`. */
export function isDecimalString(value: unknown): value is string {
  return typeof value === 'string' && DECIMAL_RE.test(value);
}

/** A cart line is usable when its quantity is a decimal string > 0 and its price a decimal string;
 *  anything else (stale / hand-edited storage) must be dropped rather than fed to `Decimal`. */
export function isValidLineData(qty: unknown, price: unknown): boolean {
  if (!isDecimalString(qty) || !isDecimalString(price)) return false;
  try {
    return new Decimal(qty).greaterThan(0);
  } catch {
    return false;
  }
}

/** Stepper increment: whole units, or half units for products sold by weight/volume. */
export function qtyStep(allowFractional: boolean): string {
  return allowFractional ? '0.5' : '1';
}

/**
 * Normalises a user-entered quantity to a canonical decimal string, or `null` when it is not a
 * positive number. Integer products are rounded to whole units, fractional ones to 3 decimals.
 */
export function sanitizeQty(value: string | number, allowFractional: boolean): string | null {
  let parsed: Decimal;
  try {
    parsed = new Decimal(typeof value === 'string' ? value.trim().replace(',', '.') : value);
  } catch {
    return null;
  }
  if (!parsed.isFinite()) return null;
  const rounded = parsed.toDecimalPlaces(allowFractional ? QTY_DECIMALS : 0, Decimal.ROUND_HALF_UP);
  if (!rounded.greaterThan(0)) return null;
  return Decimal.min(rounded, MAX_QTY).toFixed();
}

/** `true` when the input parses to a number ≤ 0 (the user meant "remove this line"). */
export function isNonPositiveQty(value: string): boolean {
  try {
    return new Decimal(value.trim().replace(',', '.')).lessThanOrEqualTo(0);
  } catch {
    return false;
  }
}

/**
 * Applies one stepper click. Going down from the minimum step yields `null` (= remove the line);
 * a quantity that is off-step (e.g. typed 1.25) snaps to the next step in the pressed direction.
 */
export function stepQty(
  current: string,
  direction: 1 | -1,
  allowFractional: boolean,
): string | null {
  const step = new Decimal(qtyStep(allowFractional));
  const value = new Decimal(current);
  const steps = value.dividedBy(step);
  const next = (direction === 1 ? steps.floor().plus(1) : steps.ceil().minus(1)).times(step);
  return sanitizeQty(next.toFixed(), allowFractional);
}

/** Line total = price × qty, rounded to money precision (bank rounding). Never uses `Number`. */
export function lineTotalOf(price: string, qty: string): string {
  return toMoneyString(round2(new Decimal(price).times(qty)));
}

/** Lines with residual bad data are skipped, so a corrupt line can never crash the render. */
export function toLineViews(cart: StoreCart | undefined): CartLineView[] {
  return Object.entries(cart ?? {})
    .filter(([, line]) => isValidLineData(line.qty, line.snapshot.price))
    .map(([productId, line]) => ({
      productId,
      qty: line.qty,
      snapshot: line.snapshot,
      lineTotal: lineTotalOf(line.snapshot.price, line.qty),
    }));
}

/** Sum of the (already rounded) line totals — the same way the server totals an order. */
export function cartTotalOf(cart: StoreCart | undefined): string {
  const total = Object.values(cart ?? {})
    .filter((line) => isValidLineData(line.qty, line.snapshot.price))
    .reduce(
      (sum, line) => sum.plus(lineTotalOf(line.snapshot.price, line.qty)),
      new Decimal(0),
    );
  return toMoneyString(total);
}

/** Number of distinct products in the cart. */
export function cartLinesCount(cart: StoreCart | undefined): number {
  return Object.keys(cart ?? {}).length;
}

/** How many of the `incoming` products a bulk add really puts into a cart that already holds
 *  `existingIds`: products already there always fit, new ones only while the cart is below the
 *  line cap (mirrors what the reducer does, in order). */
export function countAddableLines(
  existingIds: readonly string[],
  incomingIds: readonly string[],
  max: number = ORDER_LIMITS.MAX_ITEMS,
): number {
  const present = new Set(existingIds);
  let added = 0;
  for (const id of new Set(incomingIds)) {
    if (present.has(id)) {
      added += 1;
    } else if (present.size < max) {
      present.add(id);
      added += 1;
    }
  }
  return added;
}

export function isCartFull(cart: StoreCart | undefined): boolean {
  return cartLinesCount(cart) >= ORDER_LIMITS.MAX_ITEMS;
}
